import { getDateSeed } from './dateSeed';

export const DAILY_PUZZLE_GAMES = ['sudoku', '15puzzle', '2048'] as const;

export type DailyPuzzleGame = typeof DAILY_PUZZLE_GAMES[number];

export interface DailyPuzzleIdentity {
  gameType: DailyPuzzleGame;
  date: string;
  difficulty: number;
  seed: number;
}

export const formatDateString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const parseDateString = (value: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    throw new Error(`Invalid puzzle date: ${value}`);
  }

  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (formatDateString(date) !== value) {
    throw new Error(`Invalid puzzle date: ${value}`);
  }

  return date;
};

export const createDailyPuzzleIdentity = (
  gameType: DailyPuzzleGame,
  date: Date,
  difficulty: number,
  seed?: number
): DailyPuzzleIdentity => {
  const localPuzzleDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return {
    gameType,
    date: formatDateString(date),
    difficulty,
    seed: seed ?? getDateSeed(localPuzzleDate, difficulty)
  };
};

export const parseDailyPuzzleIdentity = (serialized: string): DailyPuzzleIdentity => {
  const value: unknown = JSON.parse(serialized);
  if (
    typeof value !== 'object' ||
    value === null ||
    !('gameType' in value) ||
    !('date' in value) ||
    !('difficulty' in value) ||
    !('seed' in value)
  ) {
    throw new Error('Invalid saved puzzle identity');
  }

  const identity = value as Record<string, unknown>;
  if (
    !DAILY_PUZZLE_GAMES.includes(identity.gameType as DailyPuzzleGame) ||
    typeof identity.date !== 'string' ||
    typeof identity.difficulty !== 'number' ||
    !Number.isInteger(identity.difficulty) ||
    ![1, 2, 3, 4].includes(identity.difficulty) ||
    typeof identity.seed !== 'number' ||
    !Number.isSafeInteger(identity.seed)
  ) {
    throw new Error('Invalid saved puzzle identity');
  }

  parseDateString(identity.date);
  return identity as unknown as DailyPuzzleIdentity;
};

export const puzzleIdentitiesMatch = (
  first: DailyPuzzleIdentity,
  second: DailyPuzzleIdentity
): boolean => {
  return first.gameType === second.gameType &&
    first.date === second.date &&
    first.difficulty === second.difficulty &&
    first.seed === second.seed;
};

export const canRestorePuzzleState = (
  serializedIdentity: string | null,
  savedSeed: number | undefined,
  expectedIdentity: DailyPuzzleIdentity
): boolean => {
  if (serializedIdentity === null) {
    return savedSeed === expectedIdentity.seed;
  }

  const savedIdentity = parseDailyPuzzleIdentity(serializedIdentity);
  return savedSeed === expectedIdentity.seed &&
    puzzleIdentitiesMatch(savedIdentity, expectedIdentity);
};
